import React, { useState } from 'react';
import { Badge } from '../../ui/badge';
import { Card } from '../../ui/card';
import {
  ChevronDown,
  ChevronRight,
  FileEdit,
  GraduationCap,
  Plane,
  Globe,
  Home,
  DollarSign,
  Briefcase,
  ListChecks,
  Calendar,
  BookOpen,
  FileText,
  Users,
  ClipboardList,
  MapPin,
  Shield,
  CheckCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';

// ─── Sub-service label map ──────────────────────────────────────────────────

const SUB_SERVICE_LABELS = {
  IELTS_REGISTRATION: 'IELTS Registration',
  PTE_REGISTRATION: 'PTE Registration',
  IELTS_CLASS: 'IELTS Class',
  PTE_CLASS: 'PTE Class',
  LANGUAGE_CLASS: 'Language Class',
  SOP: 'Statement of Purpose',
  LOR: 'Letter of Recommendation',
  RESEARCH_APPLICATION: 'Research Application',
  FREE_RESOURCE: 'Free Resource',
  COUNSELOR_APPOINTMENT: 'Counselor Appointment',
};

const DOC_STATUS_COLORS = {
  ready: 'bg-emerald-100 text-emerald-700',
  in_progress: 'bg-amber-100 text-amber-700',
  not_started: 'bg-gray-100 text-gray-600',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatLabel(key) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function formatValue(val) {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) return val.length > 0 ? val.join(', ') : '—';
  return String(val);
}

// ─── Collapsible Section ────────────────────────────────────────────────────

function Section({ title, icon: Icon, defaultOpen = true, accent = '#6366F1', children }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2.5 px-4 py-3 bg-gray-50/80 hover:bg-gray-100/60 transition-colors text-left"
      >
        <Icon className="w-4 h-4 shrink-0" style={{ color: accent }} />
        <span className="text-sm font-semibold text-gray-800 flex-1">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-4 py-3 border-t border-gray-100">{children}</div>}
    </div>
  );
}

function FieldRow({ label, value }) {
  return (
    <div className="flex justify-between items-start py-1.5 border-b border-gray-50 last:border-0 gap-4">
      <span className="text-xs text-gray-500 shrink-0">{label}</span>
      <span className="text-xs font-medium text-gray-800 text-right break-words">{formatValue(value)}</span>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SERVICE-SPECIFIC VIEWERS
// ═════════════════════════════════════════════════════════════════════════════

function PreappViewer({ formData, accent }) {
  const subs = formData.selectedSubServices || [];
  const details = formData.subServiceDetails || {};

  return (
    <div className="space-y-3">
      <Section title="Selected Sub-Services" icon={ListChecks} accent={accent}>
        <div className="flex flex-wrap gap-1.5 mb-1">
          {subs.map((s) => (
            <Badge key={s} variant="secondary" className="text-xs font-medium">
              {SUB_SERVICE_LABELS[s] || s}
            </Badge>
          ))}
          {subs.length === 0 && <span className="text-xs text-gray-400">None selected</span>}
        </div>
      </Section>

      {subs.map((sub) => {
        const subDetails = details[sub];
        if (!subDetails || Object.keys(subDetails).length === 0) return null;
        return (
          <Section
            key={sub}
            title={SUB_SERVICE_LABELS[sub] || sub}
            icon={BookOpen}
            accent={accent}
            defaultOpen={false}
          >
            {Object.entries(subDetails).map(([key, val]) => (
              <FieldRow key={key} label={formatLabel(key)} value={val} />
            ))}
          </Section>
        );
      })}

      {formData.generalNotes && (
        <Section title="General Notes" icon={FileText} accent={accent} defaultOpen={false}>
          <p className="text-xs text-gray-700 whitespace-pre-wrap">{formData.generalNotes}</p>
        </Section>
      )}
    </div>
  );
}

function ApplyUniversityViewer({ formData, accent }) {
  const programs = formData.selectedPrograms || [];
  const appDetails = formData.applicationDetails || {};
  const docStatus = appDetails.documentStatus || {};

  return (
    <div className="space-y-3">
      <Section title={`Selected Programs (${programs.length})`} icon={GraduationCap} accent={accent}>
        {programs.length > 0 ? (
          <div className="space-y-2">
            {programs.map((p, i) => (
              <div key={p.programId || i} className="rounded-lg bg-gray-50 px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-900">
                    #{p.priority || i + 1} {p.programName || 'Unnamed'}
                  </span>
                  {p.source && (
                    <Badge variant="outline" className="text-[10px]">
                      {p.source === 'direct' ? 'Direct' : p.source === 'wishlist' ? 'Wishlist' : 'Manual'}
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {p.universityName || '—'} {p.country && `\u00B7 ${p.country}`} {p.intake && `\u00B7 ${p.intake}`}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-xs text-gray-400">No programs selected</span>
        )}
      </Section>

      {Object.keys(docStatus).length > 0 && (
        <Section title="Document Status" icon={FileText} accent={accent} defaultOpen={false}>
          {Object.entries(docStatus).map(([doc, status]) => (
            <div key={doc} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
              <span className="text-xs text-gray-600 capitalize">{formatLabel(doc)}</span>
              <Badge className={`text-[10px] ${DOC_STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
                {status === 'ready' ? 'Ready' : status === 'in_progress' ? 'In Progress' : 'Not Started'}
              </Badge>
            </div>
          ))}
        </Section>
      )}

      {(appDetails.sopReadiness || appDetails.budgetRange || appDetails.applicationNotes) && (
        <Section title="Application Details" icon={ClipboardList} accent={accent} defaultOpen={false}>
          {appDetails.sopReadiness && <FieldRow label="SOP Readiness" value={formatLabel(appDetails.sopReadiness)} />}
          {appDetails.budgetRange && <FieldRow label="Budget Range" value={appDetails.budgetRange} />}
          {appDetails.scholarshipInterest !== undefined && <FieldRow label="Scholarship Interest" value={appDetails.scholarshipInterest} />}
          {appDetails.applicationNotes && <FieldRow label="Notes" value={appDetails.applicationNotes} />}
        </Section>
      )}
    </div>
  );
}

function VisaViewer({ formData, accent }) {
  return (
    <div className="space-y-3">
      <Section title="Visa Details" icon={Plane} accent={accent}>
        <FieldRow label="Destination Country" value={formData.destinationCountry} />
        <FieldRow label="Visa Type" value={formatLabel(formData.visaType || '')} />
        <FieldRow label="University" value={formData.universityName} />
        <FieldRow label="Program" value={formData.programName} />
        <FieldRow label="Passport Expiry" value={formData.passportExpiry} />
        <FieldRow label="Preferred Embassy" value={formData.preferredEmbassy} />
        <FieldRow label="Offer Letter Received" value={formData.offerLetterReceived} />
        <FieldRow label="Previous Visa Rejection" value={formData.previousVisaRejection} />
        <FieldRow label="Mock Interview Needed" value={formData.needMockInterview} />
      </Section>
      {formData.additionalNotes && (
        <Section title="Notes" icon={FileText} accent={accent} defaultOpen={false}>
          <p className="text-xs text-gray-700 whitespace-pre-wrap">{formData.additionalNotes}</p>
        </Section>
      )}
    </div>
  );
}

function TravelViewer({ formData, accent }) {
  return (
    <div className="space-y-3">
      <Section title="Travel Details" icon={Globe} accent={accent}>
        <FieldRow label="From" value={formData.departureCity} />
        <FieldRow label="To" value={`${formData.destinationCity || '—'}, ${formData.destinationCountry || '—'}`} />
        <FieldRow label="Departure Date" value={formData.preferredDepartureDate} />
        <FieldRow label="University" value={formData.universityName} />
        <FieldRow label="Budget" value={formData.budgetRange} />
        <FieldRow label="Airport Pickup" value={formData.airportPickupNeeded} />
        <FieldRow label="Travel Insurance" value={formData.travelInsuranceNeeded} />
      </Section>
      {(formData.emergencyContactName || formData.emergencyContactPhone) && (
        <Section title="Emergency Contact" icon={Shield} accent={accent} defaultOpen={false}>
          <FieldRow label="Name" value={formData.emergencyContactName} />
          <FieldRow label="Phone" value={formData.emergencyContactPhone} />
          <FieldRow label="Relationship" value={formData.emergencyContactRelation} />
        </Section>
      )}
    </div>
  );
}

function AccommodationViewer({ formData, accent }) {
  return (
    <div className="space-y-3">
      <Section title="Housing Preferences" icon={Home} accent={accent}>
        <FieldRow label="City" value={formData.destinationCity} />
        <FieldRow label="Country" value={formData.destinationCountry} />
        <FieldRow label="University" value={formData.universityName} />
        <FieldRow label="Move-in Date" value={formData.moveInDate} />
        <FieldRow label="Type" value={formatLabel(formData.accommodationType || '')} />
        <FieldRow label="Monthly Budget" value={formData.monthlyBudget} />
        <FieldRow label="Room Preference" value={formatLabel(formData.roomPreference || '')} />
        <FieldRow label="Max Commute" value={formData.maxCommuteMinutes ? `${formData.maxCommuteMinutes} min` : '—'} />
        <FieldRow label="Furnished" value={formData.furnished} />
        <FieldRow label="Open to Roommates" value={formData.sharedAccommodationOk} />
      </Section>
      {formData.additionalNotes && (
        <Section title="Special Requirements" icon={FileText} accent={accent} defaultOpen={false}>
          <p className="text-xs text-gray-700 whitespace-pre-wrap">{formData.additionalNotes}</p>
        </Section>
      )}
    </div>
  );
}

function LoanViewer({ formData, accent }) {
  return (
    <div className="space-y-3">
      <Section title="Loan Details" icon={DollarSign} accent={accent}>
        <FieldRow label="University" value={formData.universityName} />
        <FieldRow label="Program" value={formData.programName} />
        <FieldRow label="Country" value={formData.destinationCountry} />
        <FieldRow label="Duration" value={formData.programDuration} />
        <FieldRow label="Total Cost" value={formData.totalCostEstimate} />
        <FieldRow label="Loan Amount Needed" value={formData.loanAmountNeeded} />
        <FieldRow label="Offer Letter" value={formData.offerLetterReceived} />
        <FieldRow label="Collateral Available" value={formData.collateralAvailable} />
      </Section>
      {formData.hasCoApplicant && formData.coApplicant && (
        <Section title="Co-Applicant" icon={Users} accent={accent} defaultOpen={false}>
          <FieldRow label="Relationship" value={formData.coApplicant.relationship} />
          <FieldRow label="Occupation" value={formData.coApplicant.occupation} />
          <FieldRow label="Monthly Income" value={formData.coApplicant.monthlyIncome} />
          <FieldRow label="Income Proof" value={formatLabel(formData.coApplicant.incomeProofType || '')} />
        </Section>
      )}
      {formData.additionalNotes && (
        <Section title="Notes" icon={FileText} accent={accent} defaultOpen={false}>
          <p className="text-xs text-gray-700 whitespace-pre-wrap">{formData.additionalNotes}</p>
        </Section>
      )}
    </div>
  );
}

function JobsViewer({ formData, accent }) {
  return (
    <div className="space-y-3">
      <Section title="Job Search Details" icon={Briefcase} accent={accent}>
        <FieldRow label="Country" value={formData.destinationCountry} />
        <FieldRow label="Job Type" value={formatLabel(formData.jobType || '')} />
        <FieldRow label="Field" value={formData.fieldOfInterest} />
        <FieldRow label="University" value={formData.universityName} />
        <FieldRow label="Program" value={formData.enrolledProgram} />
        <FieldRow label="Experience" value={formData.workExperience} />
        <FieldRow label="Skills" value={formData.skills} />
        <FieldRow label="LinkedIn" value={formData.linkedInUrl} />
        <FieldRow label="Min Hourly Rate" value={formData.minimumHourlyRate} />
        <FieldRow label="LinkedIn Optimization" value={formData.needLinkedInOptimization} />
        <FieldRow label="Interview Prep" value={formData.needInterviewPrep} />
      </Section>
      {formData.additionalNotes && (
        <Section title="Notes" icon={FileText} accent={accent} defaultOpen={false}>
          <p className="text-xs text-gray-700 whitespace-pre-wrap">{formData.additionalNotes}</p>
        </Section>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════

const SERVICE_TYPE_ACCENT = {
  PROFILE_ASSESSMENT: '#6366F1',
  UNIVERSITY_SHORTLISTING: '#14B8A6',
  APPLICATION_ASSISTANCE: '#8B5CF6',
  VISA_GUIDANCE: '#F43F5E',
  SCHOLARSHIP_SEARCH: '#F59E0B',
  ACCOMMODATION_HELP: '#06B6D4',
  LOAN_ASSISTANCE: '#84CC16',
  PRE_DEPARTURE_ORIENTATION: '#F97316',
};

const SERVICE_TYPE_LABEL = {
  PROFILE_ASSESSMENT: 'Profile Assessment',
  UNIVERSITY_SHORTLISTING: 'Pre-application Support',
  APPLICATION_ASSISTANCE: 'Apply University',
  VISA_GUIDANCE: 'Visa & Interview Support',
  SCHOLARSHIP_SEARCH: 'Ticket & Travel Support',
  ACCOMMODATION_HELP: 'Find Accommodation',
  LOAN_ASSISTANCE: 'Education Loan',
  PRE_DEPARTURE_ORIENTATION: 'Find Jobs Abroad',
};

/**
 * FormDataViewer - Read-only display of pre-application form data.
 * Used in Admin, Counselor, Agent, and Student dashboards.
 *
 * @param {{ serviceType: string, formData: object }} props
 */
export default function FormDataViewer({ serviceType, formData }) {
  if (!formData) return null;

  const accent = SERVICE_TYPE_ACCENT[serviceType] || '#6366F1';
  const label = SERVICE_TYPE_LABEL[serviceType] || 'Service';

  const renderViewer = () => {
    switch (serviceType) {
      case 'UNIVERSITY_SHORTLISTING':
        return <PreappViewer formData={formData} accent={accent} />;
      case 'APPLICATION_ASSISTANCE':
        return <ApplyUniversityViewer formData={formData} accent={accent} />;
      case 'VISA_GUIDANCE':
        return <VisaViewer formData={formData} accent={accent} />;
      case 'SCHOLARSHIP_SEARCH':
        return <TravelViewer formData={formData} accent={accent} />;
      case 'ACCOMMODATION_HELP':
        return <AccommodationViewer formData={formData} accent={accent} />;
      case 'LOAN_ASSISTANCE':
        return <LoanViewer formData={formData} accent={accent} />;
      case 'PRE_DEPARTURE_ORIENTATION':
        return <JobsViewer formData={formData} accent={accent} />;
      default:
        // Generic fallback: render all keys
        return (
          <Section title="Application Details" icon={ClipboardList} accent={accent}>
            {Object.entries(formData).map(([key, val]) => (
              <FieldRow key={key} label={formatLabel(key)} value={typeof val === 'object' ? JSON.stringify(val) : val} />
            ))}
          </Section>
        );
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="w-4 h-4" style={{ color: accent }} />
        <h4 className="text-sm font-bold text-gray-800">Pre-Application Form</h4>
        <Badge variant="outline" className="text-[10px] ml-auto" style={{ borderColor: accent, color: accent }}>
          {label}
        </Badge>
      </div>
      {renderViewer()}
    </div>
  );
}
